import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { getTraceabilityStats } from '@/services/traceabilityService';

export default function BatchTraceabilityPage() {
  const navigate = useNavigate();

  const [stats, setStats] = useState({ batchesTracked: 0, posWithTrace: 0 });
  const [recentTraces, setRecentTraces] = useState<Array<{ type: string; batch: string; detail: string; date: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const res = await getTraceabilityStats();
        if (res && res.success) {
          setStats(res.stats || { batchesTracked: 0, posWithTrace: 0 });
          setRecentTraces(res.recentTraces || []);
        }
      } catch (err) {
        console.error('Failed to load traceability stats:', err);
      } finally {
        setLoading(false);
      }
    }
    loadStats();
  }, []);

  return (
    <AppLayout>
      <div className="min-h-screen bg-[#f8fafc] py-6 px-4 md:px-6 w-full">
        <div className="w-full">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#1e293b]">Batch Traceability</h1>
              <p className="text-sm text-[#64748b] mt-0.5">Forward and backward trace for quality &amp; recall management</p>
            </div>
            
            {/* Quick Actions at Top Right End */}
            <div className="flex items-center gap-2.5 flex-wrap sm:ml-auto">
              {/* <button
                onClick={() => navigate('/manufacturing/traceability/forward')}
                className="px-4 py-2 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm"
              >
                <i className="ri-arrow-right-circle-line text-base" /> Forward Trace
              </button>
              <button
                onClick={() => navigate('/manufacturing/traceability/backward')}
                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm"
              >
                <i className="ri-arrow-left-circle-line text-base" /> Backward Trace
              </button> */}
              <button
                onClick={() => navigate('/manufacturing/traceability/recall')}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1.5 shadow-sm"
              >
                <i className="ri-alert-line text-base" /> Recall Simulation
              </button>
            </div>
          </div>

          {/* Two Main Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
            {/* Forward Trace Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 relative overflow-hidden group hover:border-sky-300 transition-all shadow-sm">
              <div className="absolute top-0 right-0 w-32 h-32 bg-sky-50 rounded-bl-full opacity-60" />
              <div className="relative">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-sky-100 mb-4">
                  <i className="ri-arrow-right-circle-line text-2xl text-sky-600" />
                </div>
                <h2 className="text-lg font-bold text-[#1e293b] mb-1">Forward Trace</h2>
                <p className="text-sm text-[#64748b] mb-4">Track where a raw material batch was used across production orders</p>
                <div className="bg-sky-50/80 border border-sky-100 rounded-xl p-3.5 mb-5">
                  <div className="flex items-center gap-2 text-xs font-medium text-sky-800">
                    <i className="ri-information-line text-sky-600" />
                    <span>Enter a raw material batch number (GRN batch) to see the orders and finished batches it went into</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/manufacturing/traceability/forward')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#2563eb] hover:bg-blue-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                >
                  <i className="ri-search-line" />
                  Start Forward Trace
                </button>
              </div>
            </div>

            {/* Backward Trace Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 relative overflow-hidden group hover:border-indigo-300 transition-all shadow-sm">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50 rounded-bl-full opacity-60" />
              <div className="relative">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-indigo-100 mb-4">
                  <i className="ri-arrow-left-circle-line text-2xl text-indigo-600" />
                </div>
                <h2 className="text-lg font-bold text-[#1e293b] mb-1">Backward Trace</h2>
                <p className="text-sm text-[#64748b] mb-4">Find all raw materials and batches that went into a finished goods batch</p>
                <div className="bg-indigo-50/80 border border-indigo-100 rounded-xl p-3.5 mb-5">
                  <div className="flex items-center gap-2 text-xs font-medium text-indigo-800">
                    <i className="ri-information-line text-indigo-600" />
                    <span>Enter a finished goods batch or invoice number to see the material batches it was made from</span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/manufacturing/traceability/backward')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap shadow-sm"
                >
                  <i className="ri-search-line" />
                  Start Backward Trace
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Batches Tracked</div>
              <div className="text-2xl font-bold text-[#1e293b]">{stats.batchesTracked}</div>
              <div className="text-xs text-slate-400 mt-1 font-medium">Unique input + output batches</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider mb-1">Production Orders with Traceability</div>
              <div className="text-2xl font-bold text-[#1e293b]">{stats.posWithTrace}</div>
              <div className="text-xs text-slate-400 mt-1 font-medium">POs with batch links recorded</div>
            </div>
            <div className="bg-white border border-emerald-200 rounded-xl p-5 shadow-sm bg-emerald-50/30">
              <div className="text-xs text-emerald-700 font-semibold uppercase tracking-wider mb-1">Recall Simulation Available</div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-emerald-100 border border-emerald-200 text-emerald-800 text-sm font-bold mt-1">
                <i className="ri-checkbox-circle-fill text-base text-emerald-600" /> READY
              </div>
              <div className="text-xs text-slate-500 mt-1 font-medium">Based on batch link data</div>
            </div>
          </div>

          {/* Recent Trace History */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-5 py-4 border-b border-slate-200 bg-slate-50">
              <h3 className="text-sm font-bold text-[#1e293b]">
                <i className="ri-history-line mr-2 text-slate-500" />
                Recent Trace History
              </h3>
            </div>
            <div className="divide-y divide-slate-100">
              {recentTraces.map((trace, i) => (
                <div key={i} className="px-5 py-3.5 flex items-center gap-4 hover:bg-slate-50 transition-colors">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-semibold border ${trace.type === 'Forward' ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-indigo-50 text-indigo-700 border-indigo-200'}`}>
                    <i className={trace.type === 'Forward' ? 'ri-arrow-right-circle-line' : 'ri-arrow-left-circle-line'} />
                    {trace.type}
                  </span>
                  <span className="text-sm text-[#1e293b] font-bold">{trace.batch}</span>
                  <span className="text-sm text-slate-600 font-medium">— {trace.detail}</span>
                  <span className="ml-auto text-xs text-slate-400 font-medium">{trace.date}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}